// Add-minutes-link workflow modal. Single URL input + Save / Cancel.
// Validates the URL and dedupes against the meeting's existing
// evidenceLinks by URL — the dialog blocks submit and renders an
// inline "Already linked" message rather than silently no-op'ing, so
// the user has a clear signal that the URL is already attached. Calls
// onConfirm with the merged evidenceLinks array; the parent owns the
// PATCH.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import type { EvidenceLink } from "../../../../types";

export interface AddMinutesLinkResult {
  url: string;
  evidenceLinks: EvidenceLink[];
}

export interface AddMinutesLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: AddMinutesLinkResult) => void | Promise<unknown>;
  meetingName: string;
  currentEvidenceLinks: EvidenceLink[];
}

function isValidUrl(s: string): boolean {
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export default function AddMinutesLinkDialog({
  isOpen,
  onClose,
  onConfirm,
  meetingName,
  currentEvidenceLinks,
}: AddMinutesLinkDialogProps) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Pattern 0: clear the URL and error when the dialog opens so a previous
  // entry does not persist across invocations.
  const [seededFor, setSeededFor] = useState(false);
  if (isOpen && !seededFor) {
    setSeededFor(true);
    setUrl("");
    setError(null);
  }
  if (!isOpen && seededFor) {
    setSeededFor(false);
  }

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    if (!isValidUrl(trimmed)) {
      setError("Enter a valid URL (http or https).");
      return;
    }
    if (currentEvidenceLinks.some((l) => l.url === trimmed)) {
      setError("Already linked.");
      return;
    }
    setError(null);
    await onConfirm({
      url: trimmed,
      evidenceLinks: [
        ...currentEvidenceLinks,
        { label: "Minutes", url: trimmed },
      ],
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add minutes link">
      <div className="space-y-4">
        <p className="text-body-sm text-slate-600 dark:text-slate-300">
          Attach the minutes URL for {meetingName}.
        </p>
        <div>
          <label htmlFor="addminutes-url" className="label">
            URL <span aria-hidden="true" className="text-red-600">*</span>
          </label>
          <input
            id="addminutes-url"
            type="url"
            className="input"
            placeholder="https://…"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (error) setError(null);
            }}
            aria-label="URL"
            aria-invalid={!!error}
          />
          {error && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
}
